/**
 * <read-more>: shows "Read more" only when the clamped description hides more than two lines
 * (feature 013, specs/013-product-form-controls/data-model.md §3).
 * The clamp itself is CSS (component-read-more.css); this element only measures and toggles.
 */
if (!customElements.get('read-more')) {
  class ReadMore extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector('.read-more__content');
      this.button = this.querySelector('.read-more__toggle');
      if (!this.content || !this.button) return;

      this.onToggle = this.toggle.bind(this);
      this.onFocusIn = () => {
        if (this.dataset.state === 'collapsed') this.setExpanded(true);
      };
      this.button.addEventListener('click', this.onToggle);
      this.content.addEventListener('focusin', this.onFocusIn);

      this.measure();
      // Re-check after resizes, rotation, late fonts and an accordion opening (zero size while closed)
      this.observer = new ResizeObserver(() => {
        if (this.dataset.state === 'expanded' || this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.measure();
        });
      });
      this.observer.observe(this.content);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.button?.removeEventListener('click', this.onToggle);
      this.content?.removeEventListener('focusin', this.onFocusIn);
    }

    measure() {
      if (this.content.clientHeight === 0) return; // hidden (closed accordion): wait for the resize
      const previous = this.dataset.state;
      this.dataset.state = 'measuring';
      const hidden = this.content.scrollHeight - this.content.clientHeight;
      const line = parseFloat(getComputedStyle(this.content).lineHeight) || 24;
      if (hidden <= 2 * line) {
        this.dataset.state = 'fits';
        this.button.hidden = true;
      } else {
        this.dataset.state = previous === 'expanded' ? 'expanded' : 'collapsed';
        this.button.hidden = false;
      }
    }

    toggle() {
      this.setExpanded(this.dataset.state !== 'expanded');
    }

    setExpanded(expanded) {
      this.dataset.state = expanded ? 'expanded' : 'collapsed';
      this.button.setAttribute('aria-expanded', String(expanded));
      this.button.textContent = expanded ? this.button.dataset.labelLess : this.button.dataset.labelMore;
      if (!expanded && this.content.getBoundingClientRect().top < 0) {
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.content.scrollIntoView({ block: 'start', behavior: reduced ? 'auto' : 'smooth' });
      }
    }
  }

  customElements.define('read-more', ReadMore);
}
